import type { Ref } from 'vue';
import {
  ref, watch, onMounted,
} from 'vue';
import type {
  Label, Rect, Staff, Unit, Pixel,
} from '@/data';
import { LabelRects } from '@/components/canvas/LabelRects';
import { FilterStyleGenerator } from '@/logic/FilterStyleGenerator';
import type { Image3DPageData, SourceType } from '@/data/Source';
import type { SyncLabel } from '@/synchronization-logic/SynchronizationActions';

export interface UseLabelWatcherProps {
  label: Label | SyncLabel;
  staves: Staff[];
  pixelUnit: Unit<Pixel>;
  hasMargin: boolean;
  isSelected: boolean;
  pages: Array<Image3DPageData>;
  currentPage?: number;
  sourceType: SourceType;
}

interface UseLabelWatcherReturn {
  rects: Ref<Rect[]>;
  isMomentBar: Ref<boolean>;
  classes: Ref<string>;
}

export const labelWatcherProps = {
  label: { type: Object, required: true },
  staves: { type: Array, required: true },
  pixelUnit: { type: Object, required: true },
  hasMargin: { type: Boolean, required: true },
  isSelected: { type: Boolean },
  currentPage: { type: Number, default: 0 },
  pages: { type: Array, required: true },
  sourceType: { type: String, required: true },
};

export function useLabelWatcher(props: UseLabelWatcherProps): UseLabelWatcherReturn {
  const rects = ref<Rect[]>([{
    x: 0, y: 0, width: 10, height: 30,
  }] as Rect[]);
  const isMomentBar = ref<boolean>(false);
  const renderCount = ref<number>(0);
  const classes = ref<string>('');

  // --- Watchers pour déclencher le re-render ---
  watch(
    [
      () => props.label.duration,
      () => props.label.onset,
      () => props.label.staffId,
      () => props.label.type,
      () => props.label.tag,
      () => props.isSelected,
      () => props.staves,
      () => props.pixelUnit,
      () => props.hasMargin,
      () => ('point' in props.label ? props.label.point : null),
    ],
    () => {
      renderCount.value += 1;
    },
  );

  function updateData() {
    rects.value = new LabelRects(
      props.pixelUnit,
      props.label,
      props.sourceType,
      props.pages,
      props.hasMargin,
    ).value;

    isMomentBar.value = !props.label.duration && !props.hasMargin;

    classes.value = (props.label.duration ? '' : 'label-bar ')
      + (props.label.duration || props.hasMargin ? '' : 'moment-bar ')
      + (props.isSelected ? 'selected ' : '')
      + FilterStyleGenerator.getCssClassNames(props.label);
  }

  watch(renderCount, updateData);

  onMounted(updateData);

  return {
    rects,
    isMomentBar,
    classes,
  };
}
